import { useState, useRef, useEffect, type KeyboardEvent } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { usePathname } from "@/hooks/usePathname";
import ConfirmModal from "@/components/ConfirmModal";
import {
  IconSidebarCollapse,
  IconChat,
  IconPlus,
  IconActions,
  IconReports,
  IconLookouts,
  IconAudit,
  IconKnowledge,
  IconEdit,
  IconTrash,
  IconSettings,
  IconSignOut,
  IconX,
} from "@/components/Icons";

// Path → active-class helper. NavLink's own isActive relies on React Router's
// useLocation which is broken in this app (see App.tsx usePathname comment),
// so we drive active state off the History-API-backed pathname instead.
function activeFor(currentPath: string, target: string, extra = ""): string {
  const active =
    currentPath === target || currentPath.startsWith(target + "/");
  return `as-link${extra ? " " + extra : ""}${active ? " is-active" : ""}`;
}

// Preview mode for UI Hub snapshots. When on, the sidebar shows mock
// conversations + a tidy user identity so Chat demos look populated.
const UI_ONLY_MODE =
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "").toLowerCase() === "true";

// Owen's local-dev override — SHOW_DEVBAR=true means "I'm developing
// the real app on my laptop, not previewing the Hub iframe." Real
// user + real sessions trump the mock data even when UI_ONLY_MODE is
// on (Owen keeps UI_ONLY_MODE on for the auth-bypass affordances).
const SHOW_DEVBAR =
  String(import.meta.env.VITE_SHOW_DEVBAR ?? "").toLowerCase() === "1" ||
  String(import.meta.env.VITE_SHOW_DEVBAR ?? "").toLowerCase() === "true";
const USE_MOCK_DATA = UI_ONLY_MODE && !SHOW_DEVBAR;

// Mock conversation list for UI_ONLY_MODE. Real-data path: a future
// `listConversations()` endpoint returning items like { id, title,
// when, active, live }. See data-contracts doc.
const MOCK_CONVERSATIONS = [
  { id: "c1", title: "Chase Q2 overdue invoices",       when: "now", active: true },
  { id: "c2", title: "Reschedule Friday's boiler jobs", when: "",    live:  true },
  { id: "c3", title: "Weekly ops briefing",              when: "2h" },
  { id: "c4", title: "Draft renewal to Harper & Son",    when: "Tue" },
  { id: "c5", title: "Engineer notes audit",             when: "Mon" },
  { id: "c6", title: "Bield account · overdue jobs",     when: "Mon" },
  { id: "c7", title: "Caldwell Joinery quote write-up",  when: "Fri" },
];

const MOCK_USER = {
  initials: "JS",
  name: "Jane Smith",
  tenant: "Coulson & Sons Heating",
};

// Short, readable form for a tenant ID. Raw UUIDs are dev-y; companyName
// would be ideal but it's not on the auth user. Show a 7-char prefix.
function formatTenant(t: string | null | undefined): string {
  if (!t) return "—";
  if (/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(t)) return t.slice(0, 7);
  return t;
}

// Lightweight session row for the conversation list.
export interface SidebarSession {
  id: string;
  title: string;
  createdAt: number;
  /** Last activity (send/receive/open) — drives the relative-time label
   *  and sort order. Falls back to createdAt when absent. */
  updatedAt?: number;
  isActive: boolean;
}

interface SidebarNewProps {
  workflowCount?: number;
  reportCount?: number;
  watchdogCount?: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onNewChat?: () => void;
  sessions?: SidebarSession[];
  /** True once the first chats fetch completes. While false, we render
   *  skeleton rows in the conversation list instead of either the
   *  real chats OR an empty state — so the user never sees a flash
   *  of "nothing here" before the data lands. */
  chatsLoaded?: boolean;
  activeChatId?: string;
  onSwitchChat?: (id: string) => void;
  onDeleteChat?: (id: string) => void;
  onRenameChat?: (id: string, title: string) => void;
  /** Mobile drawer open state. The CSS picks this up via data-mobile-open
   *  to slide the sidebar in/out from the left. */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

// Relative time for the conversation row — "just now", "12m", "3h", "Tue".
function relTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  if (diff < 7 * 86_400_000) {
    return new Date(ts).toLocaleDateString("en-GB", { weekday: "short" });
  }
  return new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function SidebarNew({
  workflowCount,
  reportCount,
  watchdogCount,
  collapsed,
  onToggleCollapse,
  onNewChat,
  sessions,
  chatsLoaded,
  activeChatId,
  mobileOpen,
  onMobileClose,
  onSwitchChat,
  onDeleteChat,
  onRenameChat,
}: SidebarNewProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const path = usePathname();
  const isOnChat = path.startsWith("/app/chat");
  // id currently being renamed via the inline input. null = not editing.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const editInputRef = useRef<HTMLInputElement | null>(null);
  // Pending delete target — drives the ConfirmModal. null = closed.
  const [pendingDelete, setPendingDelete] = useState<SidebarSession | null>(null);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  const startRename = (s: SidebarSession) => {
    setEditingId(s.id);
    setEditingValue(s.title || "New Chat");
  };
  const commitRename = () => {
    if (editingId && onRenameChat) onRenameChat(editingId, editingValue);
    setEditingId(null);
    setEditingValue("");
  };
  const cancelRename = () => {
    setEditingId(null);
    setEditingValue("");
  };
  const handleEditKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") commitRename();
    if (e.key === "Escape") cancelRename();
  };

  const initials = USE_MOCK_DATA
    ? MOCK_USER.initials
    : user
      ? (user.firstName?.[0] ?? user.email[0]).toUpperCase()
      : "?";
  const displayName = USE_MOCK_DATA
    ? MOCK_USER.name
    : user
      ? user.firstName || user.email.split("@")[0] || user.email
      : "Signed out";
  const tenant = USE_MOCK_DATA ? MOCK_USER.tenant : formatTenant(user?.tenantId);

  const handleSignOut = () => {
    logout();
    navigate("/login");
  };

  // Close the mobile drawer whenever the user follows a nav link.
  const closeMobile = () => {
    if (mobileOpen && onMobileClose) onMobileClose();
  };

  return (
    <aside
      className={`as-side${collapsed ? " is-collapsed" : ""}${mobileOpen ? " is-mobile-open" : ""}`}
      data-mobile-open={mobileOpen ? "true" : "false"}
      onClick={(e) => {
        // Close on link clicks (anchor tags) but not on buttons/inputs
        const t = e.target as HTMLElement;
        if (t.closest("a")) closeMobile();
      }}
    >
      <div className="as-brand">
        <NavLink to="/" className="as-brand-left">
          <img src="/foreman-ui/foreman-logo.png" alt="" />
          <span className="as-brand-text">Foreman</span>
        </NavLink>
        {/* Mobile-only close. Visible while drawer is open at ≤880px. */}
        <button
          className="as-mobile-close"
          title="Close sidebar"
          aria-label="Close sidebar"
          type="button"
          onClick={() => onMobileClose?.()}
        >
          <IconX width={16} height={16} />
        </button>
        <button
          className="as-brand-btn"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          type="button"
          onClick={onToggleCollapse}
        >
          <IconSidebarCollapse width={14} height={14} />
        </button>
      </div>

      <nav className="as-nav" style={{ marginTop: 12 }} data-tour="sidebar">
        <div className="as-chat-row" data-tour="sidebar-chat">
          <NavLink
            to="/app/chat"
            className={activeFor(path, "/app/chat", "as-link-chat")}
          >
            <IconChat strokeWidth={1.8} />
            <span className="as-link-label">Chat</span>
          </NavLink>
          <button
            type="button"
            onClick={onNewChat}
            title="New chat"
            aria-label="New chat"
            className="as-chat-new"
          >
            <IconPlus width={12} height={12} />
          </button>
        </div>
        <NavLink to="/app/actions" className={activeFor(path, "/app/actions")} data-tour="sidebar-actions">
          <IconActions strokeWidth={1.8} />
          <span className="as-link-label">Actions</span>
          {typeof workflowCount === "number" && workflowCount > 0 && (
            <em>{workflowCount}</em>
          )}
        </NavLink>
        <NavLink to="/app/reports" className={activeFor(path, "/app/reports")} data-tour="sidebar-reports">
          <IconReports strokeWidth={1.8} />
          <span className="as-link-label">Reports</span>
          {typeof reportCount === "number" && reportCount > 0 && (
            <em>{reportCount}</em>
          )}
        </NavLink>
        <NavLink to="/app/lookouts" className={activeFor(path, "/app/lookouts")} data-tour="sidebar-lookouts">
          <IconLookouts strokeWidth={1.8} />
          <span className="as-link-label">Lookouts</span>
          {typeof watchdogCount === "number" && watchdogCount > 0 && (
            <em>{watchdogCount}</em>
          )}
        </NavLink>
        <NavLink to="/app/audit" className={activeFor(path, "/app/audit")} data-tour="sidebar-audit">
          <IconAudit strokeWidth={1.8} />
          <span className="as-link-label">Audit</span>
        </NavLink>
        <NavLink to="/app/knowledge" className={activeFor(path, "/app/knowledge")} data-tour="sidebar-knowledge">
          <IconKnowledge strokeWidth={1.8} />
          <span className="as-link-label">Knowledge</span>
        </NavLink>
      </nav>

      {isOnChat && (
        <>
          <div className="as-section-label" style={{ marginTop: 16 }}>
            Conversations
          </div>
          <div className="as-convo-list">
            {USE_MOCK_DATA && (!sessions || sessions.length === 0) ? (
              // UI Hub preview fallback when no real sessions yet
              MOCK_CONVERSATIONS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`as-convo-item${c.active ? " is-active" : ""}${
                    c.live ? " is-live" : ""
                  }`}
                >
                  <span className="as-convo-title">{c.title}</span>
                  {c.when && <span className="as-convo-when">{c.when}</span>}
                </button>
              ))
            ) : !chatsLoaded ? (
              // Loading — render 5 skeleton rows. Each row shows ONLY a
              // title-shaped block (no right-hand "when" blob), all the
              // same width. Fades out when chatsLoaded flips true.
              [0, 1, 2, 3, 4].map((i) => (
                <div key={`skel-${i}`} className="as-convo-skel" aria-hidden>
                  <span className="as-convo-skel-title" />
                </div>
              ))
            ) : (
              (sessions ?? []).map((s) => {
                const isEditing = editingId === s.id;
                const isActive = s.id === activeChatId;
                return (
                  <div
                    key={s.id}
                    className={`as-convo-item as-convo-row${isActive ? " is-active" : ""}`}
                    onClick={() => !isEditing && onSwitchChat?.(s.id)}
                    title={s.title}
                  >
                    {isEditing ? (
                      <input
                        ref={editInputRef}
                        className="as-convo-edit"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onBlur={commitRename}
                        onKeyDown={handleEditKey}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <>
                        <span className="as-convo-title">{s.title || "New Chat"}</span>
                        <span className="as-convo-when as-convo-when-default">{relTime(s.updatedAt ?? s.createdAt)}</span>
                        <span className="as-convo-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="as-convo-icon-btn"
                            title="Rename"
                            onClick={() => startRename(s)}
                          >
                            <IconEdit width={13} height={13} />
                          </button>
                          <button
                            type="button"
                            className="as-convo-icon-btn as-convo-icon-danger"
                            title="Delete"
                            onClick={() => setPendingDelete(s)}
                          >
                            <IconTrash width={13} height={13} />
                          </button>
                        </span>
                      </>
                    )}
                  </div>
                );
              })
            )}
            {/* Loaded with zero chats — render nothing. The list area
                stays empty (no placeholder text). */}
          </div>
        </>
      )}

      <div className="as-bottom">
        <div className="as-section-label">Account</div>
        <nav className="as-nav">
          <NavLink to="/app/settings" className={activeFor(path, "/app/settings")}>
          <IconSettings strokeWidth={1.8} />
          <span className="as-link-label">Settings</span>
        </NavLink>
      </nav>

      <div className="as-user">
        <span className="as-user-avatar">{initials}</span>
        <div className="as-user-body">
          <div className="as-user-name">{displayName}</div>
          <div className="as-user-tenant">{tenant}</div>
        </div>
        <button
          className="as-user-btn"
          title="Sign out"
          type="button"
          onClick={handleSignOut}
        >
          <IconSignOut width={14} height={14} />
        </button>
      </div>
      </div>
      <ConfirmModal
        open={pendingDelete !== null}
        title="Delete conversation?"
        message={`This will permanently delete "${pendingDelete?.title || "New Chat"}". You can't undo this.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (pendingDelete) onDeleteChat?.(pendingDelete.id);
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </aside>
  );
}
